import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Clock,
  Calendar,
  User,
  Users,
  Building2,
  Package,
  FileText,
  AlertTriangle,
  Printer,
  History,
  ChevronRight,
  ArrowLeft,
  QrCode,
  Edit,
  BarChart2,
  ExternalLink,
  CheckCircle,
  XCircle,
  MessageSquare,
  X
} from 'lucide-react';

const GlassCard = ({ children, className = '' }) => (
  <div className={`bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 ${className}`}>
    {children}
  </div>
);

const StatusBadge = ({ status }) => {
  const colors = {
    'Active': 'bg-emerald-100 text-emerald-700 border border-emerald-200',
    'Issue Reported': 'bg-rose-100 text-rose-700 border border-rose-200',
    'Expired': 'bg-amber-100 text-amber-700 border border-amber-200',
    'Resolved': 'bg-blue-100 text-blue-700 border border-blue-200'
  };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[status]}`}>
      {status}
    </span>
  );
};

const BatchDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [reportIssue, setReportIssue] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Mock fetch batch data
    const fetchBatchData = () => {
      setLoading(true);
      // Simulate API call
      setTimeout(() => {
        const mockBatch = {
          id,
          batchNumber: `CK1M2024${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`,
          product: 'Sauce Base',
          location: 'Central Kitchen 1',
          date: new Date().toISOString(),
          shift: 'Morning',
          quantity: 250,
          chef: 'Ahmed Ali',
          team: ['Sara Khan', 'Mohammed Hassan'],
          qualityChecks: [
            { parameter: 'Temperature', value: '72', unit: '°C', status: 'pass' },
            { parameter: 'Moisture', value: '65', unit: '%', status: 'pass' },
            { parameter: 'Color', value: 'Standard', unit: '', status: 'pass' }
          ],
          notes: 'Standard production batch with no deviations',
          temperature: '22',
          humidity: '45',
          expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'Active',
          ingredients: [
            { name: 'Tomato Paste', quantity: '50', unit: 'kg' },
            { name: 'Spice Mix', quantity: '5', unit: 'kg' },
            { name: 'Salt', quantity: '2', unit: 'kg' }
          ],
          history: [
            {
              date: new Date().toISOString(),
              action: 'Batch Created',
              user: 'Ahmed Ali',
              details: 'Initial batch creation'
            },
            {
              date: new Date().toISOString(),
              action: 'Quality Check',
              user: 'Sara Khan',
              details: 'All parameters within specifications'
            }
          ],
          labelsPrinted: 3
        };
        setBatch(mockBatch);
        setLoading(false);
      }, 1000);
    };

    fetchBatchData();
  }, [id]);

  const handlePrintLabel = () => {
    setShowLabelModal(true);
  };

  const LabelPreview = () => (
    <div className="p-6 border rounded-lg bg-white">
      <div className="mb-4 text-center">
        <h3 className="text-lg font-bold">{batch.product}</h3>
        <QrCode className="mx-auto my-4" size={120} />
        <p className="font-mono text-lg">{batch.batchNumber}</p>
      </div>
      <div className="space-y-2 text-sm">
        <p><strong>Production Date:</strong> {new Date(batch.date).toLocaleDateString()}</p>
        <p><strong>Expiry Date:</strong> {new Date(batch.expiryDate).toLocaleDateString()}</p>
        <p><strong>Location:</strong> {batch.location}</p>
        <p><strong>Quantity:</strong> {batch.quantity} units</p>
      </div>
    </div>
  );

  const IssueReport = () => (
    <form className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Issue Type
        </label>
        <select className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500">
          <option>Quality Concern</option>
          <option>Temperature Deviation</option>
          <option>Product Defect</option>
          <option>Other</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          rows={4}
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
          placeholder="Describe the issue in detail..."
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Action Taken
        </label>
        <textarea
          rows={2}
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
          placeholder="Describe any immediate actions taken..."
        />
      </div>
    </form>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Batch Not Found</h2>
          <p className="text-gray-500 mt-2">The requested batch could not be found.</p>
          <button
            onClick={() => navigate('/batch-control')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Mobile version
  if (isMobile) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Mobile Header */}
        <div className="sticky top-0 bg-white border-b z-40">
          <div className="px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/batch-control')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-semibold">Batch Details</h1>
                <p className="text-sm text-gray-500">{batch?.batchNumber}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Content */}
        <div className="p-4 space-y-4 pb-24">
          {/* Status and Actions */}
          <div className="flex justify-between items-center">
            <StatusBadge status={batch?.status} />
            <div className="flex gap-2">
              <button
                onClick={handlePrintLabel}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <Printer className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate(`/batch-control/${id}/edit`)}
                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
              >
                <Edit className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Basic Info Card */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Product</p>
                  <p className="font-medium">{batch?.product}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-medium">{batch?.location}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Production Date</p>
                  <p className="font-medium">
                    {new Date(batch?.date).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Shift</p>
                  <p className="font-medium">{batch?.shift}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500">Chef in Charge</p>
                <p className="font-medium">{batch?.chef}</p>
              </div>
            </div>
          </div>

          {/* Quality Checks Card */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Quality Checks</h2>
            <div className="space-y-3">
              {batch?.qualityChecks.map((check, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="text-sm text-gray-500">{check.parameter}</p>
                    <p className="font-medium">
                      {check.value} {check.unit}
                    </p>
                  </div>
                  {check.status === 'pass' ? (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-rose-500" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Ingredients Card */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Ingredients</h2>
            <div className="space-y-2">
              {batch?.ingredients.map((ingredient, index) => (
                <div key={index} className="flex justify-between items-center py-2 border-b last:border-0">
                  <span className="text-gray-900">{ingredient.name}</span>
                  <span className="text-gray-600">
                    {ingredient.quantity} {ingredient.unit}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Batch History Card */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Batch History</h2>
            <div className="space-y-4">
              {batch?.history.map((event, index) => (
                <div key={index} className="pb-4 border-b last:border-0">
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-medium text-gray-900">{event.action}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(event.date).toLocaleDateString()}
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">{event.details}</p>
                  <p className="text-sm text-gray-500 mt-1">By {event.user}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Action Buttons */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4">
          <div className="flex gap-4">
            <button
              onClick={() => setReportIssue(true)}
              className="flex-1 px-4 py-3 bg-rose-50 text-rose-700 rounded-xl flex items-center justify-center gap-2"
            >
              <AlertTriangle className="w-5 h-5" />
              Report Issue
            </button>
            <button
              onClick={handlePrintLabel}
              className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-xl flex items-center justify-center gap-2"
            >
              <FileText className="w-5 h-5" />
              Generate Report
            </button>
          </div>
        </div>

        {/* Keep existing modals */}
        {showLabelModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold">Print Label</h3>
                  <button
                    onClick={() => setShowLabelModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="mb-6">
                  <LabelPreview />
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => setShowLabelModal(false)}
                    className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      // Handle print logic
                      setShowLabelModal(false);
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Print
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {reportIssue && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold">Report Issue</h3>
                  <button
                    onClick={() => setReportIssue(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="mb-6">
                  <IssueReport />
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => setReportIssue(false)}
                    className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      // Handle issue report submission
                      setReportIssue(false);
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Submit Report
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Keep existing desktop return statement
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/batch-control')}
            className="p-2 hover:bg-white/50 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Batch Details</h1>
            <p className="text-gray-500">Batch Number: {batch.batchNumber}</p>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <StatusBadge status={batch.status} />
          <div className="flex gap-4">
            <button
              onClick={handlePrintLabel}
              className="px-4 py-2 bg-white/50 text-gray-700 rounded-lg hover:bg-white/80 transition-colors flex items-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Print Label
            </button>
            <button
              onClick={() => navigate(`/batch-control/${id}/edit`)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              Edit Batch
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-8">
        {/* Left Column - Basic Info */}
        <div className="col-span-2 space-y-8">
          <GlassCard className="p-6">
            <h2 className="text-xl font-semibold mb-6">Basic Information</h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Product</p>
                <p className="font-medium">{batch.product}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Location</p>
                <p className="font-medium">{batch.location}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Production Date</p>
                <p className="font-medium">
                  {new Date(batch.date).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Shift</p>
                <p className="font-medium">{batch.shift}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Quantity</p>
                <p className="font-medium">{batch.quantity} units</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Expiry Date</p>
                <p className="font-medium">
                  {new Date(batch.expiryDate).toLocaleDateString()}
                </p>
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <h2 className="text-xl font-semibold mb-6">Production Team</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Chef in Charge</p>
                <p className="font-medium">{batch.chef}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Team Members</p>
                <div className="space-y-2">
                  {batch.team.map((member, index) => (
                    <p key={index} className="font-medium">{member}</p>
                  ))}
                </div>
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <h2 className="text-xl font-semibold mb-6">Quality Checks</h2>
            <div className="space-y-4">
              {batch.qualityChecks.map((check, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">{check.parameter}</p>
                    <p className="font-medium">
                      {check.value} {check.unit}
                    </p>
                  </div>
                  {check.status === 'pass' ? (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-rose-500" />
                  )}
                </div>
              ))}
            </div>
          </GlassCard>
        </div>

        {/* Right Column - History and Actions */}
        <div className="space-y-8">
          <GlassCard className="p-6">
            <h2 className="text-xl font-semibold mb-6">Batch History</h2>
            <div className="space-y-4">
              {batch.history.map((event, index) => (
                <div key={index} className="pb-4 border-b last:border-0">
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-medium text-gray-900">{event.action}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(event.date).toLocaleDateString()}
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">{event.details}</p>
                  <p className="text-sm text-gray-500 mt-1">By {event.user}</p>
                </div>
              ))}
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <h2 className="text-xl font-semibold mb-6">Quick Actions</h2>
            <div className="space-y-4">
              <button
                onClick={() => setReportIssue(true)}
                className="w-full px-4 py-3 bg-rose-50 text-rose-700 rounded-lg hover:bg-rose-100 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  <span>Report Issue</span>
                </div>
                <ChevronRight className="w-5 h-5" />
              </button>
              <button
                onClick={handlePrintLabel}
                className="w-full px-4 py-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-between"
              >
          <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  <span>Generate Report</span>
                </div>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <h2 className="text-xl font-semibold mb-6">Ingredients</h2>
            <div className="space-y-4">
              {batch.ingredients.map((ingredient, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-gray-900">{ingredient.name}</span>
                  <span className="text-gray-600">
                    {ingredient.quantity} {ingredient.unit}
                  </span>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>
      </div>

      {/* Print Label Modal */}
      {showLabelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">Print Label</h3>
                <button
                  onClick={() => setShowLabelModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mb-6">
                <LabelPreview />
              </div>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowLabelModal(false)}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Handle print logic
                    setShowLabelModal(false);
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Print
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Issue Modal */}
      {reportIssue && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">Report Issue</h3>
                <button
                  onClick={() => setReportIssue(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mb-6">
                <IssueReport />
              </div>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setReportIssue(false)}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Handle issue report submission
                    setReportIssue(false);
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Submit Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchDetail;